import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { enableContextualFeedbackBot } from '@gonfalon/dogfood-flags/dogfood-flags';
import { isReader as isReaderProfile } from '@gonfalon/members/isReader';
import { Box } from '@launchpad-ui/box';
import { type ButtonProps, DialogTrigger, Popover, type PopoverProps } from '@launchpad-ui/components';

import { ContextualFeedbackActive } from './ContextualFeedbackActive';
import { ContextualFeedbackButton } from './ContextualFeedbackButton';
import { ContextualFeedbackModal } from './ContextualFeedbackModal';
import { FeedbackForm } from './internal/FeedbackForm';

import styles from './ContextualFeedbackTrigger.module.css';

type SharedTriggerProps = {
  /**
   * Recipient flag key. Feedback is submitted to the qualitative feedback product via the SDK and tied to this flag,
   * and it also scopes the trigger's tracking and dismissal state.
   */
  flagKey: string;
};

type PassiveTriggerProps = SharedTriggerProps & {
  /** @default 'passive' */
  widgetVariant?: 'passive';
  /** Visual style of the trigger button. @default 'minimal' */
  variant?: 'minimal' | 'default' | 'primary';
  /** When true, renders a smaller button with only an icon. @default false */
  isCompact?: boolean;
  /** Additional CSS class name to apply to the button. */
  className?: string;
  /** Additional CSS class name to apply to the container wrapping the trigger. */
  containerClassName?: string;
  /**
   * Controls how the feedback form is displayed.
   * - 'popover': Shows the form in a popover attached to the button.
   * - 'modal': Shows the form in a centered modal dialog.
   * @default 'popover'
   */
  mode?: 'popover' | 'modal';
  /** Popover placement relative to the trigger button. Only applies when mode is 'popover'. */
  placement?: PopoverProps['placement'];
  eventData?: Record<string, string>;
} & Omit<ButtonProps, 'children' | 'variant' | 'className'>;

type ActiveTriggerProps = SharedTriggerProps & {
  widgetVariant: 'active';
  /** When true, the widget becomes eligible to appear. Pass true once the user has meaningfully progressed through the flow. */
  isComplete: boolean;
  /** The sentiment question shown in the widget's first step. */
  prompt: string;
};

export type ContextualFeedbackTriggerProps = PassiveTriggerProps | ActiveTriggerProps;

/**
 * Renders contextual feedback UI. Use `variant` to choose between passive and active modes.
 *
 * - `widgetVariant="passive"` (default): A "Give feedback" button that opens a form in a popover or modal.
 * - `widgetVariant="active"`: A floating bottom-right widget with thumbs up/down, shown once `isComplete` is true.
 *
 * See https://launchdarkly.atlassian.net/wiki/x/4YDd5
 */
export const ContextualFeedbackTrigger = (props: ContextualFeedbackTriggerProps) => {
  const { profile } = useAccountContext();
  const isReader = isReaderProfile(profile);

  if (isReader || !enableContextualFeedbackBot()) {
    return null;
  }

  if (props.widgetVariant === 'active') {
    return <ContextualFeedbackActive flagKey={props.flagKey} isComplete={props.isComplete} prompt={props.prompt} />;
  }

  const {
    flagKey,
    variant = 'minimal',
    isCompact = false,
    className,
    mode = 'popover',
    containerClassName,
    placement,
    eventData,
    widgetVariant: _widgetVariant,
    ...buttonProps
  } = props;

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      className={containerClassName}
    >
      <DialogTrigger>
        <ContextualFeedbackButton className={className} variant={variant} isCompact={isCompact} {...buttonProps} />
        {mode === 'popover' && (
          <Popover className={styles.popover} placement={placement}>
            <>
              <Box slot="body" display="flex" flexDirection="column" alignItems="flex-start" gap="$300">
                <FeedbackForm eventData={eventData} flagKey={flagKey} />
              </Box>
            </>
          </Popover>
        )}
        {mode === 'modal' && <ContextualFeedbackModal eventData={eventData} flagKey={flagKey} />}
      </DialogTrigger>
    </Box>
  );
};
